import React from "react";
import {
  Button,
  Popover,
  Box,
  Stack,
  Typography,
  alpha,
  useTheme,
  Divider,
  IconButton,
  Paper,
  Fade,
  TextField,
} from "@mui/material";
import { Icon } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

interface QuickRange {
  label: string;
  range: [Dayjs, Dayjs];
  icon?: string;
}

interface DateRangeSelectorProps {
  onChange:
    | ((range: [Dayjs | null, Dayjs | null]) => void)
    | ((date: Dayjs | null) => void);
  value: [Dayjs | null, Dayjs | null] | Dayjs | null;
  variant?: "primary" | "light" | "transparent";
  className?: string;
  mode?: "range" | "single";
  maxDate?: Dayjs;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  onChange,
  value,
  variant = "primary",
  className,
  mode = "range",
  maxDate,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const open = Boolean(anchorEl);

  // Button styles based on variant
  const getButtonStyles = () => {
    const baseStyles = {
      px: 2,
      py: 1,
      borderRadius: 1,
      transition: "all 0.2s ease-in-out",
      minWidth: mode === "single" ? 160 : 200,
      justifyContent: "flex-start",
      boxShadow: "none",
    };

    switch (variant) {
      case "light":
        return {
          ...baseStyles,
          bgcolor: alpha(theme.palette.common.white, 0.9),
          color: theme.palette.grey[800],
          "&:hover": {
            bgcolor: theme.palette.common.white,
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        };
      case "transparent":
        return {
          ...baseStyles,
          bgcolor: alpha(theme.palette.common.white, 0.1),
          color: theme.palette.common.white,
          "&:hover": {
            bgcolor: alpha(theme.palette.common.white, 0.2),
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        };
      default: // primary
        return {
          ...baseStyles,
          bgcolor: "#1E8078",
          color: "white",
          "&:hover": {
            bgcolor: "#166F68",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        };
    }
  };

  const quickRanges: QuickRange[] = [
    {
      label: "Today",
      range: [dayjs().startOf("day"), dayjs().endOf("day")],
      icon: "today",
    },
    {
      label: "Yesterday",
      range: [
        dayjs().subtract(1, "day").startOf("day"),
        dayjs().subtract(1, "day").endOf("day"),
      ],
      icon: "history",
    },
    {
      label: "This Week",
      range: [dayjs().startOf("week"), dayjs().endOf("week")],
      icon: "view_week",
    },
    {
      label: "Last Week",
      range: [
        dayjs().subtract(1, "week").startOf("week"),
        dayjs().subtract(1, "week").endOf("week"),
      ],
      icon: "calendar_view_week",
    },
    {
      label: "This Month",
      range: [dayjs().startOf("month"), dayjs().endOf("month")],
      icon: "calendar_month",
    },
    {
      label: "Last Month",
      range: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
      icon: "calendar_today",
    },
  ];

  const quickDates = [
    { label: "Today", date: dayjs(), icon: "today" },
    { label: "Yesterday", date: dayjs().subtract(1, "day"), icon: "history" },
    {
      label: "Start of Week",
      date: dayjs().startOf("week"),
      icon: "view_week",
    },
    {
      label: "Start of Month",
      date: dayjs().startOf("month"),
      icon: "calendar_month",
    },
  ];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleQuickRangeSelect = (range: QuickRange["range"]) => {
    console.log("Quick range selected:", range);
    if (mode === "range") {
      // Ensure we're passing Dayjs objects
      const processedRange: [Dayjs | null, Dayjs | null] = [range[0], range[1]];
      console.log("Processed quick range:", processedRange);
      (onChange as (range: [Dayjs | null, Dayjs | null]) => void)(
        processedRange
      );
    }
    handleClose();
  };

  const handleQuickDateSelect = (date: Dayjs) => {
    if (mode === "single") {
      (onChange as (date: Dayjs | null) => void)(date);
    }
    handleClose();
  };

  const handleDateChange = (newDate: Dayjs | null, index?: number) => {
    console.log("handleDateChange called with:", { newDate, index });
    console.log("Current value:", value);

    if (!newDate?.isValid()) {
      console.log("Invalid date received");
      return;
    }

    if (index === undefined) {
      // Single date mode
      console.log("Single date mode - calling onChange with:", newDate);
      (onChange as (date: Dayjs | null) => void)(newDate);
    } else {
      // Range mode
      const newRange: [Dayjs | null, Dayjs | null] = [
        ...(value as [Dayjs | null, Dayjs | null]),
      ];
      newRange[index] = newDate;
      console.log("Range mode - calling onChange with:", newRange);
      (onChange as (range: [Dayjs | null, Dayjs | null]) => void)(newRange);
    }
  };

  const formatDateRange = (
    value: [Dayjs | null, Dayjs | null] | Dayjs | null
  ): string => {
    if (mode === "single") {
      const date = value as Dayjs | null;
      return date ? date.format("MMM D, YYYY") : "Select Date";
    }

    const range = value as [Dayjs | null, Dayjs | null];
    if (!range[0] || !range[1]) return "Select Date Range";
    if (range[0].isSame(range[1], "day")) {
      return range[0].format("MMM D, YYYY");
    }
    if (range[0].isSame(range[1], "month")) {
      return `${range[0].format("MMM D")} - ${range[1].format("D, YYYY")}`;
    }
    if (range[0].isSame(range[1], "year")) {
      return `${range[0].format("MMM D")} - ${range[1].format("MMM D, YYYY")}`;
    }
    return `${range[0].format("MMM D, YYYY")} - ${range[1].format(
      "MMM D, YYYY"
    )}`;
  };

  const isSelectedRange = (rangeToCheck: [Dayjs, Dayjs]): boolean => {
    if (mode !== "range") return false;
    const range = value as [Dayjs | null, Dayjs | null];
    if (!range[0] || !range[1]) return false;
    return (
      range[0].isSame(rangeToCheck[0], "day") &&
      range[1].isSame(rangeToCheck[1], "day")
    );
  };

  const isSelectedDate = (dateToCheck: Dayjs): boolean => {
    if (mode !== "single") return false;
    const date = value as Dayjs | null;
    return date?.isSame(dateToCheck, "day") || false;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Button
        onClick={handleClick}
        variant="contained"
        className={className}
        startIcon={<Icon>date_range</Icon>}
        sx={getButtonStyles()}
      >
        {formatDateRange(value)}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            width: mode === "single" ? 400 : 600,
            overflow: "hidden",
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(10px)",
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          },
        }}
      >
        <Box sx={{ p: 2, display: "flex", gap: 3 }}>
          {/* Quick Options */}
          <Paper
            elevation={0}
            sx={{
              minWidth: mode === "single" ? 150 : 200,
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.6),
              borderRadius: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "#1E8078",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon fontSize="small">schedule</Icon>
              Quick Select
            </Typography>
            <Stack spacing={1}>
              {mode === "range"
                ? quickRanges.map((quickRange) => (
                    <Button
                      key={quickRange.label}
                      variant={
                        isSelectedRange(quickRange.range) ? "contained" : "text"
                      }
                      size="small"
                      startIcon={<Icon>{quickRange.icon}</Icon>}
                      onClick={() => handleQuickRangeSelect(quickRange.range)}
                      sx={{
                        justifyContent: "flex-start",
                        color: isSelectedRange(quickRange.range)
                          ? "white"
                          : "text.primary",
                        backgroundColor: isSelectedRange(quickRange.range)
                          ? "#1E8078"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isSelectedRange(quickRange.range)
                            ? "#166F68"
                            : alpha("#1E8078", 0.1),
                        },
                        transition: "all 0.2s ease",
                        borderRadius: 0.5,
                      }}
                    >
                      {quickRange.label}
                    </Button>
                  ))
                : quickDates.map((quickDate) => (
                    <Button
                      key={quickDate.label}
                      variant={
                        isSelectedDate(quickDate.date) ? "contained" : "text"
                      }
                      size="small"
                      startIcon={<Icon>{quickDate.icon}</Icon>}
                      onClick={() => handleQuickDateSelect(quickDate.date)}
                      sx={{
                        justifyContent: "flex-start",
                        color: isSelectedDate(quickDate.date)
                          ? "white"
                          : "text.primary",
                        backgroundColor: isSelectedDate(quickDate.date)
                          ? "#1E8078"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isSelectedDate(quickDate.date)
                            ? "#166F68"
                            : alpha("#1E8078", 0.1),
                        },
                        transition: "all 0.2s ease",
                        borderRadius: 0.5,
                      }}
                    >
                      {quickDate.label}
                    </Button>
                  ))}
            </Stack>
          </Paper>

          <Divider orientation="vertical" flexItem />

          {/* Calendar */}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#1E8078",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Icon fontSize="small">edit_calendar</Icon>
                {mode === "range" ? "Custom Range" : "Select Date"}
              </Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main,
                  },
                }}
              >
                <Icon>close</Icon>
              </IconButton>
            </Box>
            <Stack spacing={3}>
              {mode === "range" ? (
                <>
                  <DatePicker
                    label="Start Date"
                    value={Array.isArray(value) ? value[0] : null}
                    onChange={(newValue) =>
                      handleDateChange(newValue as Dayjs | null, 0)
                    }
                    maxDate={maxDate || undefined}
                    renderInput={(params) => (
                      <TextField {...params} size="small" fullWidth />
                    )}
                    // sx={{
                    //   "& .MuiPickersDay-root.Mui-selected": {
                    //     backgroundColor: "#1E8078",
                    //     "&:hover": {
                    //       backgroundColor: "#166F68",
                    //     },
                    //   },
                    //   "& .MuiPickersDay-root:hover": {
                    //     backgroundColor: alpha("#1E8078", 0.1),
                    //   },
                    //   "& .MuiPickersDay-today": {
                    //     borderColor: "#1E8078",
                    //   },
                    // }}
                  />
                  <DatePicker
                    label="End Date"
                    value={Array.isArray(value) ? value[1] : null}
                    onChange={(newValue) =>
                      handleDateChange(newValue as Dayjs | null, 1)
                    }
                    minDate={
                      Array.isArray(value) ? value[0] || undefined : undefined
                    }
                    maxDate={maxDate || undefined}
                    renderInput={(params) => (
                      <TextField {...params} size="small" fullWidth />
                    )}
                  />
                </>
              ) : (
                <DatePicker
                  label="Select Date"
                  value={mode === "single" ? (value as Dayjs | null) : null}
                  onChange={(newValue) =>
                    handleDateChange(newValue as Dayjs | null)
                  }
                  maxDate={maxDate || undefined}
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                  // sx={{
                  //   "& .MuiPickersDay-root.Mui-selected": {
                  //     backgroundColor: "#1E8078",
                  //   "&:hover": {
                  //     backgroundColor: "#166F68",
                  //   },
                  // },
                  // "& .MuiPickersDay-root:hover": {
                  //   backgroundColor: alpha("#1E8078", 0.1),
                  // },
                  // "& .MuiPickersDay-today": {
                  //   borderColor: "#1E8078",
                  // },
                  // }}
                />
              )}
            </Stack>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};
